// @ts-strict-ignore
import React from 'react';
import type { QueryKey } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { Workflow, WorkflowVersion } from '~/flow/types';
import type { ResponseException } from '~/helpers/exceptionHandler';
import { getMatrixModels } from '~/flow/matrix-models/services';
import apiCalls from '~/helpers/apiCalls';
import { ONE_MINUTE_IN_MS } from '~/helpers/constants/time';
import { UserContext } from '~/dashboard/context';
import type {
  LegacyAction,
  ServiceCredentials,
  ServiceObject,
  WorkflowPropsResponse,
} from '../store/engine/types';
import type { ServiceConfigOption } from '../../settings/service-configuration/types';
import { getFormattedPublishedAttributes } from '../../settings/published-attributes/utils';
import type { FeatureFlags } from '~/helpers/typescript';
import { ajaxFetch, typhonAjaxFetch } from '~/helpers/api';
import { routes, typhonRoutes } from '~/helpers/routes';
import { getCustomListsWithVersions } from '../service/actions/services';
import { isInSnippetEditor } from 'flow/editor/components/helpers';
import type { API } from '~/helpers/api-new/modules';
import useAPI from '~/helpers/api-new/context';
import type { Services } from '../store/engine/types/schema';
import { addAggregationFilterOptionsToAggregationsService } from './formatters/addAggregationFilterOptionsToAggregationsService';

type UseWorkflowPropsOptions = {
  versionId: WorkflowVersion['application_version_id'];
  workflowToken: Workflow['application_token'];
  // Controls whether the workflow props query should automatically execute
  enabled?: boolean;
};

type GetWorkflowPropsOptions = UseWorkflowPropsOptions & {
  /**
   * the library API uses the new api module that is tied to the Context of the application.
   * the `getWorkflowProps` function is not tied to a component or hook lifecycle, so it needs
   * to receive the API as a param. by passing the whole API instead of a single fetcher we
   * add future support for any similar requirements.
   */
  appAPI: API;
  isEmployee: boolean;
  typhonDomain: string;
  canFetchPublishedAttributes: boolean;
  featureFlags: Partial<FeatureFlags>;
};

async function getWorkflowProps({
  appAPI,
  canFetchPublishedAttributes,
  featureFlags,
  isEmployee,
  typhonDomain,
  versionId,
  workflowToken,
}: GetWorkflowPropsOptions): Promise<WorkflowPropsResponse> {
  const [
    baseWorkflowProps,
    // some fields come in the data endpoint, so we must make a dedicated api call to it
    workflowData,
    reasonCodes,
    unformattedPublishedAttributes,
    cloneData,
    customLists = [],
    allLocalAndGlobalSnippets,
    { data_objects: clientQueryDataConfigObjects },
  ] = await Promise.all([
    apiCalls.workflows.getWorkflowProps({ workflowToken }),
    apiCalls.workflows.getWorkflowVersion({
      applicationToken: workflowToken, publicVersion: versionId,
    }),
    apiCalls.settings.getReasonCodes({ typhonDomain }),
    canFetchPublishedAttributes && apiCalls.publishedAttributes.list({ typhonDomain }),
    isEmployee && apiCalls.workflows.getCloningData(),
    featureFlags.customListsService && apiCalls.customLists.getAllLists(typhonDomain),
    featureFlags.ruleLibraryPhase >= 2 && appAPI.library.listAllLocalAndGlobalSnippets(),
    apiCalls.clientQueryDataConfig.getObjects({ typhonDomain }),
  ]);

  const updatedServices: Services = baseWorkflowProps.services;
  const actionPromises = Object.values(workflowData.actions)
    .map(async (action: LegacyAction) => {
      if (action.type === 'run') {
        const serviceData: ServiceObject = Object.values(
          updatedServices as { [id: number]: ServiceObject },
        ).find(
          (service: ServiceObject) => (service.name === action.name));
        if (serviceData) {
          const serviceDataCredSchema = updatedServices[serviceData.id].credentialsSchema;
          if (!serviceDataCredSchema) {
            const serviceCredentialsSchema: ServiceCredentials = await ajaxFetch(
              routes.v3.services(serviceData.internal_name));
            updatedServices[serviceData.id] = {
              ...updatedServices[serviceData.id],
              credentialsSchema: serviceCredentialsSchema,
            };
          }
        }
      }
    });
  await Promise.all(actionPromises);

  const configOptionsByServiceId: Record<number, ServiceConfigOption[]> = await typhonAjaxFetch(
    `${typhonDomain}${typhonRoutes.settings.customerServiceConfigOptions}`,
  );
  // Add serviceConfigOptions to updatedServices if missing
  for (const serviceId of Object.keys(configOptionsByServiceId)) {
    if (updatedServices[serviceId] && !updatedServices[serviceId].serviceConfigOptions) {
      updatedServices[serviceId] = {
        ...updatedServices[serviceId],
        serviceConfigOptions: configOptionsByServiceId[serviceId] || [],
      };
    }
  }

  const formattedServicesById = featureFlags.decisionOnEverything
    ? addAggregationFilterOptionsToAggregationsService({
      clientQueryDataConfigObjects,
      servicesById: updatedServices,
    })
    : updatedServices;


  const matrixModels = await getMatrixModels(typhonDomain, workflowData);

  // need to pull all major versions of each custom list used in the workflow
  const transformedCustomLists = (customLists?.length > 0)
    ? await getCustomListsWithVersions({ typhonDomain, workflowData, customLists }) : [];

  const publishedAttributes = canFetchPublishedAttributes
    ? getFormattedPublishedAttributes(
      unformattedPublishedAttributes.published_attributes,
      featureFlags,
    )
    : [];

  const reasonCodeGroupsWithCodeData = reasonCodes
    ? baseWorkflowProps.reasonCodeGroups.map(reasonCodeGroup => {
      const codesGroupWithDetails = reasonCodeGroup.reasonCodes.map(reasonCode => {
        const matchingReasonCode = reasonCodes.find(rc => rc.reason_code_id === reasonCode);
        if (matchingReasonCode) {
          return matchingReasonCode;
        }

        return reasonCode;
      });

      return {
        ...reasonCodeGroup,
        reasonCodes: codesGroupWithDetails,
      };
    })
    : baseWorkflowProps.reasonCodeGroups;


  /**
   * If a Service within the reason code group is disabled, the Daffy endpoint will return its
   * service name and the Reason Codes within them as `null`. This transformation filters these
   * `null` values out to avoid having to deal with them throughout our system
   */
  const reasonCodeGroupsWithoutNullData = reasonCodeGroupsWithCodeData.map(rcg => {
    const reasonCodesWithoutNulls = rcg.reasonCodes?.filter(rc => rc !== null) ?? [];
    const serviceNamesWithoutNulls = rcg.services?.filter(service => service !== null) ?? [];

    return {
      archived: rcg.archived,
      id: rcg.id,
      name: rcg.name,
      reasonCodes: reasonCodesWithoutNulls,
      services: serviceNamesWithoutNulls,
    };
  });

  return {
    allWorkflows: baseWorkflowProps.applications,
    categories: baseWorkflowProps.categories,
    customLists: transformedCustomLists,
    customers: cloneData.customers,
    importableAttributes: baseWorkflowProps.importableAttributes,
    lists: baseWorkflowProps.lists,
    matrixModels,
    outcomes: workflowData.outcomes,
    publishedAttributes,
    reasonCodeGroups: reasonCodeGroupsWithoutNullData,
    reasonCodes,
    recurringWorkflows: baseWorkflowProps.recurringWorkflows,
    sandboxPersonas: baseWorkflowProps.sandboxPersonas || [],
    services: formattedServicesById,
    snippets: allLocalAndGlobalSnippets || [],
    tagTypes: baseWorkflowProps.tagTypes || [],
    tags: workflowData.tags,
    workflow: baseWorkflowProps.application,
  };
}

function getQueryKey({
  workflowToken,
  versionId,
}: Partial<Pick<UseWorkflowPropsOptions, 'workflowToken' | 'versionId'>> = {}): QueryKey {
  /**
   * if no token is passed, a key that invalidates all workflow props queries is returned,
   * not just the one specific to a token.
   */
  return [
    'workflow.props',
    ...(workflowToken ? [workflowToken] : []),
    ...(versionId ? [versionId] : []),
  ];
}

/**
 * Hook that returns all props bundled together. The existing Daffy BE endpoints
 * send back account-level data such as tags and outcomes over the /data endpoint and
 * other dedicated endpoints such as published attributes. Ideally, that info would be part
 * of the workflow props since these are part of the data that the workflow editor needs
 * to support building policies. Hence, this hook makes all necessary calls and returns a
 * complete props object with the expected data.
 */
function useWorkflowProps(options: UseWorkflowPropsOptions) {
  const {
    agent: { isEmployee }, typhonDomain, featureFlags, permissions, breadcrumbs,
  } = React.useContext(UserContext);

  const api = useAPI();

  const canFetchPublishedAttributes = Boolean(isEmployee || permissions['READ:PublishedAttributes:null']);
  const inSnippetEditor = isInSnippetEditor(breadcrumbs);
  // TODO - BUILD-3998 - remove library FF as dependency
  const shouldDisableAutomaticRefetching = featureFlags.useTyphonWorkflowEndpoints
    || inSnippetEditor;
  const { enabled = true } = options;

  return useQuery<WorkflowPropsResponse, ResponseException>({
    queryKey: getQueryKey(options),
    queryFn: () => getWorkflowProps({
      ...options,
      appAPI: api,
      canFetchPublishedAttributes,
      featureFlags,
      isEmployee,
      typhonDomain,
    }),
    retry: false,
    refetchOnReconnect: false,
    ...(shouldDisableAutomaticRefetching && { refetchOnWindowFocus: false }),
    staleTime: shouldDisableAutomaticRefetching ? Infinity : 5 * ONE_MINUTE_IN_MS,
    enabled,
  });
}


useWorkflowProps.getQueryKey = getQueryKey;
export default useWorkflowProps;
